import { Button } from '@synoptic/ui-kit/button/button.js';
import { CheckIcon } from '@synoptic/ui-kit/icons/react/check.js';
import { LockIcon } from '@synoptic/ui-kit/icons/react/lock.js';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.js';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { StreamPackageCardFragmentFragment } from '@/__generated__/graphql';
import { Stream, StreamPackage } from '@/__generated__/schema-types.ts';
import { paths } from '@/routes-utils/paths';
import { getMonthlyPrice } from '@/utils/paid-data.ts';
import {
  HomeCardNameAndDescription,
  HomeStreamAuthor,
  HomeStreamCardCategoryBadge,
  HomeStreamCardFollowersBlock,
  HomeStreamCardPriceTag,
} from './home-stream-card';
import {
  homeStreamPackageAdditionalInfo,
  homeStreamPackageAuthorInfo,
  streamPackageCard,
  streamPackageCardBenefit,
  streamPackageCardBenefits,
  streamPackageCardFooter,
  streamPackageCardHeader,
  streamPackageCardLeft,
  streamPackageCardRight,
  streamPackageCardStreamItem,
  streamPackageCardTitle,
} from './home-streams-section.css';

const VisibleStream = ({ stream }: { stream: Stream }) => {
  return (
    <div
      key={stream.id + '-in-package'}
      className={streamPackageCardStreamItem}
    >
      <HomeStreamCardCategoryBadge category={stream.streamCategory} />
      <HomeCardNameAndDescription
        name={stream.name}
        description={stream.description}
        size="small"
        rowLimit={{
          name: 1,
          description: 1,
        }}
      />
    </div>
  );
};

export const HomeStreamPackageCard = ({
  streamPackage,
}: {
  streamPackage: StreamPackageCardFragmentFragment & {
    includedStreams?: StreamPackage['includedStreams'];
  };
}) => {
  const isMobile = !useIsTablet();
  const navigate = useNavigate();

  const benefits = [
    'Curated stream collection',
    'Instant API access with easy setup',
  ];

  const visibleStreams = useMemo(() => {
    return streamPackage.includedStreams?.slice(0, isMobile ? 2 : 6) || [];
  }, [streamPackage.includedStreams, isMobile]);

  const Footer = (
    <div className={streamPackageCardFooter}>
      <div className={homeStreamPackageAdditionalInfo}>
        {streamPackage.includedStreams?.length && (
          <span>{streamPackage.includedStreams?.length} streams</span>
        )}
      </div>
      <Button
        onClick={() => {
          navigate(paths.makeStreamPackage(streamPackage.id));
        }}
        startIcon={<LockIcon />}
        size="small"
        fullWidth
        variant="primary"
      >
        Unlock package
      </Button>
    </div>
  );

  return (
    <div className={streamPackageCard}>
      <div className={streamPackageCardLeft}>
        <header className={streamPackageCardHeader}>
          <h3 className={streamPackageCardTitle}>{streamPackage.name}</h3>
          <HomeStreamCardPriceTag
            price={getMonthlyPrice(streamPackage.packagePaidData)}
          />
        </header>
        <ul className={streamPackageCardBenefits}>
          {benefits.map((benefit, i) => (
            <li className={streamPackageCardBenefit} key={i}>
              <CheckIcon />
              {benefit}
            </li>
          ))}
        </ul>
        <div className={homeStreamPackageAuthorInfo}>
          <HomeStreamAuthor author={streamPackage.author} />
          <HomeStreamCardFollowersBlock
            followersCount={streamPackage.subscribersCount}
          />
        </div>
        {!isMobile && Footer}
      </div>
      <div className={streamPackageCardRight}>
        {visibleStreams?.map((stream) => (
          <VisibleStream stream={stream} key={stream.id} />
        ))}
      </div>
      {isMobile && Footer}
    </div>
  );
};
